import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserStore } from '@citiguide/frontend-core';
import type { ChatResponse, ChatStatusResponse } from '@citiguide/shared';
import { buildChatRequest, classifyChatError, postChatTurn } from '../services/chatService';
import type { ChatErrorKind } from '../services/chatService';
import { useCurationStore } from '../store/curationStore';
import { useLanguageStore } from '../store/languageStore';
import { CHAT_STATUS_KEY } from './useChatStatus';

/** After this long without an answer the thinking indicator shows in any mode. */
export const THINKING_AFTER_MS = 4000;

interface Options {
  statusMode: ChatStatusResponse['mode'] | undefined;
  /** Called with a successful response after the transcript was updated. */
  onResponse: (response: ChatResponse) => void;
}

/**
 * One chat turn. The visitor's text is only added to the transcript once the server answered, so a
 * failed turn leaves both the transcript and the input text untouched (the caller keeps the text).
 */
export function useChatTurn({ statusMode, onResponse }: Options) {
  const queryClient = useQueryClient();
  const [slow, setSlow] = useState(false);
  const [lastModel, setLastModel] = useState<ChatResponse['meta']['model'] | null>(null);
  const [error, setError] = useState<ChatErrorKind | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [cooling, setCooling] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const mutation = useMutation({ mutationFn: postChatTurn });

  useEffect(() => {
    if (!mutation.isPending) {
      setSlow(false);
      return;
    }
    const timer = setTimeout(() => setSlow(true), THINKING_AFTER_MS);
    return () => clearTimeout(timer);
  }, [mutation.isPending]);

  useEffect(
    () => () => {
      if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    },
    [],
  );

  const startCooldown = (seconds: number) => {
    if (seconds <= 0) return;
    setCooldownSeconds(seconds);
    setCooling(true);
    if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    cooldownTimer.current = setTimeout(() => setCooling(false), seconds * 1000);
  };

  /** Resolves true when the turn succeeded (the caller may then clear its input). */
  const send = useCallback(
    async (text: string): Promise<boolean> => {
      const trimmed = text.trim();
      if (!trimmed || mutation.isPending || cooling) return false;
      const { messages, state, appendMessage } = useCurationStore.getState();
      const userMessage = { role: 'user' as const, content: trimmed };
      const location = useUserStore.getState().location;
      const request = buildChatRequest({
        messages: [...messages, userMessage],
        state,
        uiLanguage: useLanguageStore.getState().currentLanguage,
        location: location ? [location[0], location[1]] : null,
      });
      setError(null);
      try {
        const response = await mutation.mutateAsync(request);
        appendMessage(userMessage);
        setLastModel(response.meta.model);
        onResponse(response);
        return true;
      } catch (err) {
        const info = classifyChatError(err);
        setError(info.kind);
        startCooldown(info.cooldownSeconds);
        if (info.kind === 'not_enabled') {
          // The tenant switched the feature off mid-session: hide the entry point.
          queryClient.setQueryData(CHAT_STATUS_KEY, null);
        }
        if (info.kind === 'unavailable') setUnavailable(true);
        return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutation.isPending, mutation.mutateAsync, cooling, onResponse, queryClient],
  );

  const isPending = mutation.isPending;
  const thinking = isPending && (statusMode === 'flash' || lastModel === 'flash_reasoning' || slow);
  /** True while the slow-wait text ("taking a moment longer") should replace the plain one. */
  const longWait = isPending && slow;

  return {
    send,
    isPending,
    thinking,
    longWait,
    error,
    cooling,
    cooldownSeconds,
    /** A 200 that was not a chat response (CDN fallback): behave like chips_only for this session. */
    unavailable,
    clearError: () => setError(null),
  };
}
